import * as THREE from 'three'
import ReactDOM from 'react-dom'
import { useEffect, useRef, useState } from 'react'
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber'
import { OrbitControls, Instances, Instance } from '@react-three/drei'
import allColors from './Colors'
import { SimpleReverb } from './SimpleReverb'
import { LayerMaterial, Color, Depth, Noise } from 'lamina/vanilla'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import './styles.css'

let damp = 0
let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
const ci1 = randI(allColors.length)
const ci2 = randI(allColors.length)
const col1 = allColors[ci1]
const col2 = allColors[ci2]
let currCol = [...col1, ...col2]
let bg = "#0c0c0f"

const material = new LayerMaterial({
  depthWrite: false,
  layers: [
    new Color({ color: '#fff' }),
    new Noise({ mapping: 'local', type: 'simplex', scale: 100000000000, colorA: 'red', colorB: 'blue', mode: 'subtract' })
  ]
})
const material2 = new LayerMaterial({
  depthTest: false,
  layers: [
    new Color({ color: rand(currCol) }),
    new Noise({ mapping: 'local', type: 'simplex', scale: 100000000000, colorA: 'black', colorB: 'black', mode: 'subtract' })
  ]
})

class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
        uniform float time;
        uniform float damp;
        uniform sampler2D tOld;
        uniform sampler2D tNew;
        varying vec2 vUv;
        vec4 when_gt( vec4 x, float y ) {
            return max( sign( x - y ), 0.000000001 );
        }
        
        void main() {
            vec2 offset = vec2(0.0, -0.001);
            vec2 uv = vUv + offset;
            vec4 texelOld = texture2D(tOld, uv);
            vec4 texelNew = texture2D(tNew, vUv);
            texelOld *= damp * when_gt( texelOld, 0.000000001 );
            gl_FragColor = max(texelNew, texelOld);
        }
        `
    }
  }
}

extend({ EffectComposer, RenderPass, AfterBurn })
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => {
    ref.current.uniforms.damp.value = damp
    damp = 1
    composer.current.render()
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}

function App() {
  const size = useWindowSize();
  useEffect(() => {
    damp = 0
  },[size.height, size.width])
  const start = () => {
    if (!audioStarted) {
      whiteNoise.start(0);
      audioStarted = true;
      fadeIn()
    }else{
      damp = 0
    }
  }
  return (
    <div id="main" onClick={() => start()} key={size.height+size.width}
    onDoubleClick={() => {
      toggleFullScreen()
    }}
    >
      <Canvas flat={true} linear={true} dpr={[1, 2]} camera={{ position: [0, 0, 1], near: 0.0001, far: 1000, fov: 45 }}
        gl={{ antialias: false }}
      >
        <Thangs />
        <color attach="background" args={[bg]} />
        <OrbitControls />
        <Tint />
      </Canvas>
    </div>
  )
}

ReactDOM.render(
  <App />,
  document.getElementById('root')
)

let c1 = rand(currCol)
let c2 = rand(currCol)
let c3 = rand(currCol)

let oFromCol = new THREE.Color(c3)
let oToCol = new THREE.Color(c3)
let fromCol = [
  new THREE.Color(c1),
  new THREE.Color(c2),
]
let toCol = [
  new THREE.Color(c1),
  new THREE.Color(c2),
]
let dt = 0
let dir = rand([1, -1])
let rS = (3+randI(7)) * 0.1
let xS = (1+randI(19)) * 0.02
let sS = (4 + randI(30)) * 0.01
let rads = (3 + randI(14)) * 0.05
let dtMult = 1 + randI(10)
let w = (4 + randI(5)) * 0.1
let bs = 0.0015*rand([1,1,1,1,1,1,2,3,4,5])
let oW = rand([0.25,0.25,0.25, 0.26,0.26,0.05,0.03,0,0.28,0.32])

function Thangs() {
  const group = useRef()
  const oGroup = useRef()
  const left = useRef()
  const right = useRef()
  useFrame((s, d) => {
    if (randI(200) > 195) {
      if (randI(10) > 7) {
        let same = rand(currCol)
        c1 = same
        c2 = same
      }
      if (randI(10) > 7) {
        c1 = rand(currCol)
      }
      if (randI(10) > 7) {
        c2 = rand(currCol)
      }
      if (randI(10) > 6) {
        c3 = rand(currCol)
      }
      toCol[0] = toCol[0].set(c1)
      toCol[1] = toCol[1].set(c2)
      oToCol = oToCol.set(c3)
    }
    fromCol[0].set(fromCol[0].lerpColors(fromCol[0], toCol[0], 0.01))
    fromCol[1].set(fromCol[1].lerpColors(fromCol[1], toCol[1], 0.008))
    oFromCol.set(oFromCol.lerpColors(oFromCol, oToCol, 0.01))
    material.layers[0].color.set(fromCol[Math.floor((dt * dtMult) % 2)])
    material2.layers[0].color.set(oFromCol)
    group.current.rotation.z = oGroup.current.rotation.z = (Math.cos(dt * rS) + (dt * 0.1*dir)) * Math.PI * rads * dir
    dt += 0.01
    group.current.position.x = oGroup.current.position.x =  Math.sin(dt * xS) * 0.1
    group.current.scale.y = Math.sin(dt * sS) * w
    left.current.position.y = Math.sin(dt * sS) * w*-oW
    right.current.position.y = Math.sin(dt * sS) * w*oW
    reverb.dry.value = 0.05+Math.abs(Math.sin(dt * rS*0.5)*0.1)
    reverb.wet.value = 0.1+(Math.abs(Math.sin(dt * sS))*0.9)
  })
  return (
    <group position={[0, -0.19, 0]}>
      <group ref={group}>
        <Instances geometry={new THREE.BoxBufferGeometry(0.003, 0.5, 0.001)} material={material}>
          <Instance />
        </Instances>
      </group>
      <group ref={oGroup}>
        <mesh ref={left} geometry={new THREE.BoxBufferGeometry(bs, bs, bs*3)} material={material2}  />
        <mesh ref={right} geometry={new THREE.BoxBufferGeometry(bs, bs, bs*3)} material={material2} />
      </group>
    </group>
  )
}

window.AudioContext = window.AudioContext || window.webkitAudioContext;
let audioContext = new AudioContext();
let audioStarted = false;
let bufferSize = 4 * audioContext.sampleRate,
  noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate),
  output = noiseBuffer.getChannelData(0);
for (let i = 0; i < bufferSize; i++) {
  output[i] = Math.random() * 2 - 1;
}

let whiteNoise = audioContext.createBufferSource();
whiteNoise.buffer = noiseBuffer;
whiteNoise.loop = true;

let reverb = SimpleReverb(audioContext);
reverb.connect(audioContext.destination);
reverb.time = 4
reverb.wet.value = 0.1
reverb.dry.value = 0.1
reverb.filterType = 'lowpass'
reverb.cutoff.value = 100

var getFrequency = function (note) {
  var notes = ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#'],
      octave,
      keyNumber;
  if (note.length === 3) {
      octave = note.charAt(2);
  } else {
      octave = note.charAt(1);
  }
  keyNumber = notes.indexOf(note.slice(0, -1));
  if (keyNumber < 3) {
      keyNumber = keyNumber + 12 + ((octave - 1) * 12) + 1; 
  } else {
      keyNumber = keyNumber + ((octave - 1) * 12) + 1; 
  }
  return 440 * Math.pow(2, (keyNumber- 49) / 12);
};

let NUM = 6;
let filters = [];
let fgains = [];
let panners = [];

let freqs = []

for (let i = 0; i < NUM; i++) {
  let f = audioContext.createBiquadFilter();
  let p = audioContext.createStereoPanner();
  p.pan.value = (randI(20)*0.1)-1;
  panners.push(p)
  f.type = "bandpass";
  f.Q.value = 200;
  let fr = getFrequency(rand(['C','D','E','F','G'])+rand([3,4,4,5,5,6]))
  freqs.push(fr)
  f.frequency.value = fr
  filters.push(f);
  let g = audioContext.createGain();
  g.gain.value = 0;
  fgains.push(g);
}
for (let i = 0; i < NUM; i++) {
  whiteNoise.connect(filters[i]);
  filters[i].connect(fgains[i]);
  fgains[i].connect(panners[i]);
  panners[i].connect(reverb);
}

function fadeIn() {
  fgains.map((_, i) => {
    fgains[i].gain.linearRampToValueAtTime(1, audioContext.currentTime + 4)
  })
}

function useWindowSize() { let [e, i] = useState({ width: void 0, height: void 0 }); return useEffect(() => { function e() { i({ width: window.innerWidth, height: window.innerHeight }) } return window.addEventListener("resize", e), e(), () => window.removeEventListener("resize", e) }, []), e }
function toggleFullScreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  } else if (document.exitFullscreen) {
    document.exitFullscreen();
  }
}

console.log('%c 🍃🍃 @ramioso 2023-02-06 🍃🍃', 'color: green');
